import React, { useState, useRef, useCallback } from 'react';

const PixelArtEditor = ({ onSave, onCancel, initialData = null }) => {
  const GRID_SIZE = 50;
  const PIXEL_SIZE = 8; // 8px per pixel for 400x400 total canvas
  
  // Initialize grid with white pixels or load from initialData
  const initializeGrid = () => {
    if (initialData) {
      return initialData;
    }
    const grid = [];
    for (let i = 0; i < GRID_SIZE; i++) {
      grid[i] = [];
      for (let j = 0; j < GRID_SIZE; j++) {
        grid[i][j] = '#FFFFFF';
      }
    }
    return grid;
  };

  const [grid, setGrid] = useState(initializeGrid);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('brush'); // 'brush' | 'fill'
  const [brushSize, setBrushSize] = useState(1); // 1 or 3
  const canvasRef = useRef(null);

  // Predefined palette of 16 classic colors
  const palette = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00',
    '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#800000', '#008000', '#000080', '#808000',
    '#800080', '#008080', '#C0C0C0', '#808080'
  ];

  const drawGrid = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw pixels
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        ctx.fillStyle = grid[i][j];
        ctx.fillRect(j * PIXEL_SIZE, i * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
        
        // Draw grid lines
        ctx.strokeStyle = '#CCCCCC';
        ctx.lineWidth = 0.5;
        ctx.strokeRect(j * PIXEL_SIZE, i * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
      }
    }
  }, [grid]);

  React.useEffect(() => {
    drawGrid();
  }, [drawGrid]);

  const getPixelPositionFromPoint = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    return {
      x: Math.floor(x / PIXEL_SIZE),
      y: Math.floor(y / PIXEL_SIZE)
    };
  };

  const getPixelPosition = (event) => getPixelPositionFromPoint(event.clientX, event.clientY);

  // Paints a square brush (1x1 or 3x3) centered on (x, y)
  const paintPixel = (x, y) => {
    const offset = Math.floor(brushSize / 2);
    const newGrid = grid.map(row => [...row]);
    let changed = false;

    for (let dy = -offset; dy <= offset; dy++) {
      for (let dx = -offset; dx <= offset; dx++) {
        const px = x + dx;
        const py = y + dy;
        if (px >= 0 && px < GRID_SIZE && py >= 0 && py < GRID_SIZE) {
          newGrid[py][px] = selectedColor;
          changed = true;
        }
      }
    }

    if (changed) setGrid(newGrid);
  };

  // Iterative 4-directional flood fill, starting from (x, y)
  const fillArea = (x, y) => {
    if (x < 0 || x >= GRID_SIZE || y < 0 || y >= GRID_SIZE) return;

    const targetColor = grid[y][x];
    if (targetColor === selectedColor) return;

    const newGrid = grid.map(row => [...row]);
    const stack = [[x, y]];

    while (stack.length > 0) {
      const [px, py] = stack.pop();
      if (px < 0 || px >= GRID_SIZE || py < 0 || py >= GRID_SIZE) continue;
      if (newGrid[py][px] !== targetColor) continue;

      newGrid[py][px] = selectedColor;
      stack.push([px + 1, py], [px - 1, py], [px, py + 1], [px, py - 1]);
    }

    setGrid(newGrid);
  };

  const applyTool = (x, y) => {
    if (tool === 'fill') {
      fillArea(x, y);
    } else {
      paintPixel(x, y);
    }
  };

  const handleMouseDown = (event) => {
    setIsDrawing(true);
    const { x, y } = getPixelPosition(event);
    applyTool(x, y);
  };

  const handleMouseMove = (event) => {
    if (!isDrawing || tool === 'fill') return;
    const { x, y } = getPixelPosition(event);
    paintPixel(x, y);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    setIsDrawing(true);
    const touch = event.touches[0];
    const { x, y } = getPixelPositionFromPoint(touch.clientX, touch.clientY);
    applyTool(x, y);
  };

  const handleTouchMove = (event) => {
    if (!isDrawing || tool === 'fill') return;
    event.preventDefault();
    const touch = event.touches[0];
    const { x, y } = getPixelPositionFromPoint(touch.clientX, touch.clientY);
    paintPixel(x, y);
  };

  const handleTouchEnd = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const newGrid = [];
    for (let i = 0; i < GRID_SIZE; i++) {
      newGrid[i] = [];
      for (let j = 0; j < GRID_SIZE; j++) {
        newGrid[i][j] = '#FFFFFF';
      }
    }
    setGrid(newGrid);
  };

  const saveArtwork = () => {
    // Convert grid to base64 image data
    const canvas = document.createElement('canvas');
    canvas.width = GRID_SIZE;
    canvas.height = GRID_SIZE;
    const ctx = canvas.getContext('2d');
    
    // Draw the artwork at 1px per pixel
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        ctx.fillStyle = grid[i][j];
        ctx.fillRect(j, i, 1, 1);
      }
    }
    
    const imageData = canvas.toDataURL('image/png');
    onSave(imageData, grid);
  };

  return (
    <div className="pixel-art-editor">
      <div className="editor-header">
        <h3>Create Your Pixel Art (50x50)</h3>
        <div className="color-picker-section">
          <label>Selected Color: </label>
          <input
            type="color"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="color-picker"
          />
          <span className="current-color" style={{ backgroundColor: selectedColor }}></span>
        </div>
      </div>

      <div className="palette">
        <div className="palette-label">Quick Colors:</div>
        <div className="palette-colors">
          {palette.map((color, index) => (
            <button
              key={index}
              className={`palette-color ${selectedColor === color ? 'selected' : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => setSelectedColor(color)}
              title={color}
            />
          ))}
        </div>
      </div>

      <div className="tools-section">
        <div className="palette-label">Tool:</div>
        <div className="tool-buttons">
          <button
            className={`tool-btn ${tool === 'brush' ? 'selected' : ''}`}
            onClick={() => setTool('brush')}
            title="Brush"
          >
            ✏️ Brush
          </button>
          <button
            className={`tool-btn ${tool === 'fill' ? 'selected' : ''}`}
            onClick={() => setTool('fill')}
            title="Fill Bucket"
          >
            🪣 Fill
          </button>
        </div>
      </div>

      <div className="tools-section">
        <div className="palette-label">Brush Size:</div>
        <div className="tool-buttons">
          <button
            className={`tool-btn ${brushSize === 1 ? 'selected' : ''}`}
            onClick={() => setBrushSize(1)}
            disabled={tool === 'fill'}
            title="1 pixel"
          >
            1px
          </button>
          <button
            className={`tool-btn ${brushSize === 3 ? 'selected' : ''}`}
            onClick={() => setBrushSize(3)}
            disabled={tool === 'fill'}
            title="3 pixels"
          >
            3px
          </button>
        </div>
      </div>

      <div className="canvas-container">
        <canvas
          ref={canvasRef}
          width={GRID_SIZE * PIXEL_SIZE}
          height={GRID_SIZE * PIXEL_SIZE}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="pixel-canvas"
        />
      </div>

      <div className="editor-controls">
        <button onClick={clearCanvas} className="clear-btn">
          Clear Canvas
        </button>
        <div className="action-buttons">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={saveArtwork} className="save-btn">
            Save Artwork
          </button>
        </div>
      </div>
    </div>
  );
};

export default PixelArtEditor; 